import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Form, Button, Container, Alert, Card } from 'react-bootstrap';
import { createFace, addMeeting } from '../api';
import { useAccessToken } from '../hooks/useAccessToken';

function AddFaceForm() {
  const navigate = useNavigate();
  const getToken = useAccessToken();
  const [formData, setFormData] = useState({
    name: '',
    linkedin_url: '',
    position: '',
    organization: '',
    email: '',
    sector: '',
    how_met: '',
    general_notes: '',
    image: null,
  });
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: files ? files[0] : value,
    }));
  };

  const handleMeetingChange = (index, field, value) => {
    const updatedMeetings = [...meetings];
    updatedMeetings[index] = { ...updatedMeetings[index], [field]: value };
    setMeetings(updatedMeetings);
  };

  const addMeetingField = () => {
    setMeetings([...meetings, { meeting_date: '', meeting_notes: '' }]);
  };

  const removeMeeting = (index) => {
    const updatedMeetings = meetings.filter((_, i) => i !== index);
    setMeetings(updatedMeetings);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(false);

    const data = new FormData();
    for (const key in formData) {
      if (formData[key]) {
        data.append(key, formData[key]);
      }
    }

    try {
      const token = await getToken();
      const response = await createFace(data, token);
      const newFaceId = response.id;

      // Add meetings
      for (const meeting of meetings) {
        if (meeting.meeting_date) {
          await addMeeting(newFaceId, {
            meeting_date: meeting.meeting_date,
            meeting_notes: meeting.meeting_notes,
          }, token);
        }
      }

      setSuccess(true);
      setTimeout(() => {
        navigate('/');
      }, 1500);
    } catch (err) {
      setError(err.message || 'Failed to add face.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container className="my-4">
      <h2>Add New Person</h2>
      {error && <Alert variant="danger">{error}</Alert>}
      {success && <Alert variant="success">Face added successfully!</Alert>}
      <Form onSubmit={handleSubmit}>
        <Form.Group className="mb-3">
          <Form.Label>Name <span className="text-danger">*</span></Form.Label>
          <Form.Control
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
            disabled={loading}
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Photo <span className="text-danger">*</span></Form.Label>
          <Form.Control
            type="file"
            name="image"
            accept="image/*"
            onChange={handleChange}
            required
            disabled={loading}
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>LinkedIn Profile URL</Form.Label>
          <Form.Control
            type="url"
            name="linkedin_url"
            value={formData.linkedin_url}
            onChange={handleChange}
            disabled={loading}
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Position / Title</Form.Label>
          <Form.Control
            type="text"
            name="position"
            value={formData.position}
            onChange={handleChange}
            disabled={loading}
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Organization</Form.Label>
          <Form.Control
            type="text"
            name="organization"
            value={formData.organization}
            onChange={handleChange}
            disabled={loading}
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Email</Form.Label>
          <Form.Control
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            disabled={loading}
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Sector</Form.Label>
          <Form.Select
            name="sector"
            value={formData.sector}
            onChange={handleChange}
            disabled={loading}
          >
            <option value="">Select...</option>
            <option value="Congress">Congress</option>
            <option value="Private Sector">Private Sector</option>
            <option value="Lobbyist">Lobbyist</option>
            <option value="Other">Other</option>
          </Form.Select>
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>How I originally met them</Form.Label>
          <Form.Control
            as="textarea"
            name="how_met"
            value={formData.how_met}
            onChange={handleChange}
            rows={3}
            disabled={loading}
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>General Notes</Form.Label>
          <Form.Control
            as="textarea"
            name="general_notes"
            value={formData.general_notes}
            onChange={handleChange}
            rows={5}
            disabled={loading}
          />
        </Form.Group>

        <h4 className="mt-4 mb-3">Meetings</h4>

        {meetings.map((meeting, index) => (
          <Card key={index} className="mb-3 p-3 border-primary">
            <Form.Group className="mb-2">
              <Form.Label>Date <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="date"
                value={meeting.meeting_date}
                onChange={(e) => handleMeetingChange(index, 'meeting_date', e.target.value)}
                disabled={loading}
                required
              />
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label>Notes</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={meeting.meeting_notes || ''}
                onChange={(e) => handleMeetingChange(index, 'meeting_notes', e.target.value)}
                disabled={loading}
              />
            </Form.Group>
            <Button
              variant="outline-danger"
              size="sm"
              onClick={() => removeMeeting(index)}
              disabled={loading}
            >
              Remove
            </Button>
          </Card>
        ))}

        <Button
          variant="outline-primary"
          onClick={addMeetingField}
          className="mb-4"
          disabled={loading}
        >
          + Add Meeting
        </Button>

        <Button variant="primary" type="submit" disabled={loading}>
          {loading ? 'Adding...' : 'Add Person'}
        </Button>
      </Form>
    </Container>
  );
}

export default AddFaceForm;
